import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, Save, MapPin, Plus } from 'lucide-react';
import { useUserStore } from '../stores/userStore';
import { supabase } from '../lib/supabase';
import { BackButton } from '../components/common/BackButton';

export function ProfilePage() {
  const navigate = useNavigate();
  const { profile, settings, fetchProfile, fetchSettings, updateProfile, updateSettings } = useUserStore();
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    phone_number: '',
    service_area: '',
    email: '',
    profile_image_url: ''
  });

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/');
        return;
      }

      await Promise.all([fetchProfile(), fetchSettings()]);
      setIsLoading(false);
    };

    checkAuth();
  }, [navigate, fetchProfile, fetchSettings]);

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        company_name: profile.company_name || '',
        phone_number: profile.phone_number || '',
        service_area: profile.service_area || '',
        email: profile.email || '',
        profile_image_url: profile.profile_image_url || ''
      });
    }
  }, [profile]);

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user logged in');

      // Upload file to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const { error: uploadError, data } = await supabase.storage
        .from('profile-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(fileName);

      // Update profile with new image URL
      await updateProfile({ profile_image_url: publicUrl });
      setFormData(prev => ({ ...prev, profile_image_url: publicUrl }));
    } catch (error) {
      console.error('Error uploading profile picture:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateProfile(formData);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  const profileCompletion = profile?.profile_completion || 0;

  return (
    <div className="min-h-screen pt-32 px-4 pb-16">
      <div className="max-w-4xl mx-auto">
        <BackButton to="/" label="Back to Home" />
        <div className="bg-black/50 border border-green-500/20 rounded-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div 
                className="relative w-16 h-16 rounded-full bg-green-500/20 overflow-hidden cursor-pointer group"
                onClick={handleProfilePictureClick}
              >
                {formData.profile_image_url ? (
                  <img 
                    src={formData.profile_image_url} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-8 h-8 text-green-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                )}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Plus className="w-6 h-6 text-white" />
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
              <div>
                <h1 className="text-2xl font-bold text-white">Profile</h1>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-32 bg-black/50 rounded-full">
                    <div 
                      className="h-2 bg-green-500 rounded-full transition-all"
                      style={{ width: `${profileCompletion}%` }}
                    />
                  </div>
                  <span className="text-green-500">{Math.round(profileCompletion)}%</span>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-green-500 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full bg-black/50 border border-green-500/20 rounded-md px-4 py-2 text-white focus:border-green-500 focus:ring-1 focus:ring-green-500"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-green-500 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone_number"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                className="w-full bg-black/50 border border-green-500/20 rounded-md px-4 py-2 text-white focus:border-green-500 focus:ring-1 focus:ring-green-500"
                placeholder="+1 (555) 123-4567"
                required
              />
            </div>

            <div>
              <h3 className="text-xl font-bold text-green-500 mb-4">Additional Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="service_area" className="block text-sm font-medium text-green-500 mb-1">
                    Service Area
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      id="service_area"
                      value={formData.service_area}
                      onChange={(e) => setFormData({ ...formData, service_area: e.target.value })}
                      className="w-full bg-black/50 border border-green-500/20 rounded-md pl-10 pr-4 py-2 text-white focus:border-green-500 focus:ring-1 focus:ring-green-500"
                      placeholder="e.g., Toronto, ON"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company_name" className="block text-sm font-medium text-green-500 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full bg-black/50 border border-green-500/20 rounded-md px-4 py-2 text-white focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    placeholder="Enter company name"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-500 text-black font-bold rounded-md hover:bg-green-400 transition-colors"
            >
              <Save className="w-5 h-5" />
              Save Changes
            </button>
          </form>

          {settings && (
            <div className="mt-12 pt-8 border-t border-green-500/20">
              <div className="flex items-center gap-4 mb-8">
                <Settings className="w-8 h-8 text-green-500" />
                <h2 className="text-2xl font-bold text-white">Account Settings</h2>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Real-time Updates</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.realtime_enabled}
                      onChange={(e) => updateSettings({ realtime_enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Offline Mode</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.offline_mode}
                      onChange={(e) => updateSettings({ offline_mode: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
              </div>

              <button
                onClick={() => supabase.auth.signOut()}
                className="w-full mt-8 px-6 py-3 bg-red-500 text-white font-bold rounded-md hover:bg-red-600 transition-colors"
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}